import { ChangeEvent, useCallback, useEffect, useState } from "react";

import { Autocomplete, AutocompleteSearchData, 
  Button, IconWrapper, InfoPanel, Select, 
  SelectEvent, TextField, Typography } from "@king-ict/eupisi-ui/components";
import { ReactComponent as ArrowRight } from "@king-ict/eupisi-ui/components/Icon/ArrowRight.svg";
import { CodeBook } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { getMunicipalityById, searchMunicipalities } from "../../api/Municipality/municipalityApi";
import { getTownById, searchTowns } from "../../api/Town/townApi";
import { useDidUpdateEffect } from "../../hooks";
import { TownCodeBook } from "../../models/Town";
import { useGetCountiesQuery } from "../../queries/codeBookQuery";

export interface TownSelectProps {
  initialValue?: number;
  title?: string;
  children?: any;
  readOnly?: boolean,
  disabled?: boolean,
  handleChange?: (value: TownCodeBook) => void,
  errorText?: string,   
  getErrorForPath?: (path: string) => string | undefined;
}
export const TownSelect = ({ initialValue, children, title, readOnly = false, disabled = false, errorText, handleChange, getErrorForPath }: TownSelectProps) => {
  const createDefaultAddress = (): TownCodeBook => {
    return {
      countyId: 0,
      countyName: "",
      municipalityId: 0,
      municipalityName: "",
      id: 0,
      name: "",
    };
  };

  const { t } = useTranslation();
  const { data: counties } = useGetCountiesQuery();
  const [municipalities, setMunicipalities] = useState<CodeBook[]>();
  const [towns, setTowns] = useState<CodeBook[]>();
  const [address, setAddress] = useState<TownCodeBook>(createDefaultAddress);

  useEffect(() => {
    (async () => {
      if ((initialValue ?? 0) > 0) {
        const result = await getTownById(initialValue!);
        setAddress(result);
      }
    })();
  }, [initialValue]);

  useEffect(() => {
    (async () => {
      // if (address?.countyId && (disabled || readOnly)) {
      //   setMunicipalities([{ id: address?.municipalityId ?? 0, name: address?.municipalityName }]);
      // }
      // else if (address?.countyId && !readOnly && !disabled) {
      //   var result = await searchMunicipalities("", [address.countyId]);
      //   setMunicipalities(result);
      // };
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address.countyId, disabled, readOnly]);

  useEffect(() => {
    (async () => {
      // if (address?.municipalityId && (disabled || readOnly)) {
      //   setTowns([{ id: address?.id ?? 0, name: address?.name }]);
      // }
      // else if (address?.municipalityId && !readOnly && !disabled) {
      //   const result = await searchTowns("", address.municipalityId, address.countyId);
      //   setTowns(result);
      // } else
      //   setTowns(undefined);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address?.municipalityId, disabled, readOnly]);

  const handleCountyChange = async (e: ChangeEvent<SelectEvent>) => {    
    var countyId = Number(e.target.value);
    var county = counties?.find(e => e.id === countyId)?.name ?? "";

    const newAddress: TownCodeBook = {
      ...address,
      countyId: countyId,
      countyName: county,
      municipalityId: 0,
      municipalityName: "",
      id: 0,
      name: "",
    };
    setAddress(newAddress);
    handleChange?.(newAddress);
  }
  const handleMunicipalityFilterChange = useCallback(async (searchData: AutocompleteSearchData) => {    
    if (searchData.text && searchData.text.length > 0) {
      const results = await searchMunicipalities(searchData.text!, [address.countyId]);
      setMunicipalities(results);
      return codeBookToSelectData(results);
    } else if (searchData.value && searchData.value > 0) {
      const result = await getMunicipalityById(+searchData.value);        
      setMunicipalities(result ? [result] : undefined);      
      
      if(result){
        const newAddress : TownCodeBook = {
          ...address,
          municipalityId: result.id,
          municipalityName: result.name
        }
        setAddress(newAddress);
        handleChange?.(newAddress);
      }      

      return codeBookToSelectData([result]);
    } else {return codeBookToSelectData([])}
  },[address.countyId, address.id]);

  const handleMunicipalityChange = async (e: ChangeEvent<SelectEvent>) => {    
    var municipalityId = Number(e.target.value);
    var municipality = municipalities?.find(e => e.id === municipalityId)?.name ?? "";

    const newAddress: TownCodeBook = {
      ...address,
      municipalityId: municipalityId,
      municipalityName: municipality,
      id: 0,
      name: "",
    }
    setAddress(newAddress);
    handleChange?.(newAddress);
  }
  const handleTownFilterChange = useCallback(async (searchData: AutocompleteSearchData) => {    
    if (searchData?.text && searchData.text.length > 0) {
      const results = await searchTowns(searchData.text!, address.municipalityId, address.countyId);
      setTowns(results);
      return codeBookToSelectData(results);
    } else if (searchData.value && searchData.value > 0) {
      const result = await getTownById(+searchData.value);
      setTowns(result ? [result] : undefined);
      return codeBookToSelectData([result]);
    } else {return codeBookToSelectData([])}
  },[address]);

  const handleTownChange = async (e: ChangeEvent<SelectEvent>) => {    
    var townId = Number(e.target.value);    
    var town = towns?.find(e => e.id === townId)?.name ?? "";
    const newAddress: TownCodeBook = {
      ...address,
      id: townId,
      name: town,
    };
    setAddress(newAddress);
    handleChange?.(newAddress);    
  }

  return (
    <>
        <Row>
          <Col md="4">
            {readOnly ?
              <TextField
                label={t("common.county")}
                value={address?.countyName}
                readonly
                plaintext
              />
              :
              <Select
                label={t("common.county")}
                name={"CountyId"}
                value={address?.countyId}
                selectData={codeBookToSelectData(counties)}
                onChange={(e) => handleCountyChange(e)}
                disabled={disabled}                
                errorText={getErrorForPath?.(`town.countyId`)}
              />}
          </Col>
          <Col md="4">
            {readOnly ?
              <TextField
                label={t("common.municipality")}
                value={address?.municipalityName}
                readonly
                plaintext
                errorText={errorText}
              />
              : <Autocomplete
                label={t("common.municipality")}
                name={"MunicipalityId"}
                value={address?.municipalityId}                
                minimumInputLength={3}
                getOptions={handleMunicipalityFilterChange}
                onChange={(e) => handleMunicipalityChange(e)}
                disabled={disabled}                
                errorText={getErrorForPath?.(`town.municipalityId`)}
              />}
          </Col>
          <Col md="4">
            {readOnly ?
              <TextField
                label={t("common.town")}
                value={address?.name}
                readonly
                plaintext
                errorText={errorText}
              />
              : <Autocomplete
                label={t("common.town")}
                name={"TownId"}              
                value={address?.id}              
                minimumInputLength={3}
                getOptions={handleTownFilterChange}
                onChange={(e) => handleTownChange(e)}
                disabled={disabled}                
                errorText={getErrorForPath?.(`town.id`)}
              />}
          </Col>
        </Row>        
        {children}
    </>
  );
}